import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Slider,
  Drawer,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OrderBookMobile from '../OrderBookMobile';
import ButtonGroup from '@mui/material/ButtonGroup';
import RangeSlider from '../AmountRange';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';

import apiClient from '../../../request/http-common';
import { setTradings } from '../../../request/Analysis/hitorySlice';

import { useMutation, useQuery } from "react-query";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1E2329',
    color: 'white',
    fontSize: '12px',
    height: '32px',
    '& fieldset': { borderColor: '#2B3139' },
    '&:hover fieldset': { borderColor: '#F7A600' },
    '&.Mui-focused fieldset': { borderColor: '#F7A600' }
  },
  '& .MuiInputLabel-root': { 
    color: '#848E9C', 
    fontSize: '11px',
    '&.Mui-focused': { color: '#F7A600' }
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: '#1E2329',
  color: 'white',
  fontSize: '12px',
  height: '32px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2B3139' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F7A600' },
  '& .MuiSvgIcon-root': { color: '#848E9C' },
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const Conditional = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tradingPlans = useSelector((state) => state.plans.tradingPlans);
  const currentPairFromState = useSelector((state) => state.currentPair.currentPair);
  const pair = currentPair || currentPairFromState;



  // Form state
  const [triggerPrice, setTriggerPrice] = useState(coinPrice || '');
  const [amount, setAmount] = useState(0);
  const [duration, setDuration] = useState('');
  const [quantitySlider, setQuantitySlider] = useState(0);
  const [error, setError] = useState('');
  
  // Confirmation drawer state
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingOrderSide, setPendingOrderSide] = useState('');

  // Leverage and margin for futures
  const [leverage, setLeverage] = useState('10');
  const [marginType, setMarginType] = useState('Cross');

  // Utility functions
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const calculateOrderValue = () => {
    const amountValue = parseFloat(amount || 0);
    return formatPrice(amountValue);
  };

  // Update order value when amount changes
  useEffect(() => {
    // This will trigger re-render when amount changes
  }, [amount]);

  const handleSliderChange = (event, newValue) => {
    setQuantitySlider(newValue);
    if (balance) {
      const newAmount = (newValue / 100) * parseFloat(balance);
      setAmount(formatPrice(newAmount));
    }
  };

  const handleAmountChange = (e) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    if (balance && newAmount) {
      const percentage = (parseFloat(newAmount) / parseFloat(balance)) * 100;
      setQuantitySlider(Math.min(percentage, 100));
    }
  };

  const handlePercentageClick = (percentage) => {
    if (balance) {
      const newAmount = (percentage / 100) * parseFloat(balance);
      setAmount(formatPrice(newAmount));
      setQuantitySlider(percentage);
    }
  };

  // Submit mutation
  const { isLoading: isSubmitting, mutate: submitOrder } = useMutation(
    async (orderData) => {
      console.log('Submitting Conditional order:', orderData);
      return await apiClient.post(`/api/start-trading`, orderData);
    },
    {
      onSuccess: (res) => {
        console.log('Conditional order response:', res.data);
        if (res.data.status === 'success') {
          dispatch(setTradings(res.data.tradings));
          localStorage.setItem('notification', JSON.stringify({
            message: `${pendingOrderSide === 'buy' ? 'Buy' : 'Sell'} Conditional order placed successfully!`,
            'type': 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          
          // Reset form
          setTriggerPrice('');
          setAmount('');
          setQuantitySlider(0);
          setError('');
          setConfirmationOpen(false);
          setPendingOrderSide('');
          
          // Redirect to trade history
          navigate('/trade-history');
        }
      },
      onError: (err) => {
        console.error('Conditional order error:', err);
        if (err.response?.data?.errors) {
          setError(Object.values(err.response.data.errors).flat().join(', '));
        } else {
          let errorMessage = err.response?.data?.message || 'Failed to place Conditional order';
          setError(errorMessage);
          localStorage.setItem('notification', JSON.stringify({
            message: errorMessage,
            'type': 'error'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
        setConfirmationOpen(false);
      },
    }
  );

  const validateOrder = () => {
    if (!triggerPrice || parseFloat(triggerPrice) <= 0) {
      setError('Please enter a valid trigger price');
      return false;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    
    if (!duration) {
      setError('Please select a duration');
      return false;
    }
    
    const amountNum = parseFloat(amount);
    const balanceNum = parseFloat(balance);
    
    if (amountNum > balanceNum) {
      setError(`Insufficient balance. Required: ${amount} USDT, Available: ${formatBalance(balance)} USDT`);
      return false;
    }
    
    return true;
  };

  const handleOrderClick = (side) => {
    console.log('Conditional order click:', side);
    setError('');
    
    if (!validateOrder()) {
      return;
    }
    
    setPendingOrderSide(side);
    setConfirmationOpen(true);
  };

  const confirmOrder = () => {
    console.log('Confirming Conditional order...');
    
    const orderData = {
      // Basic order fields
      amount: parseFloat(amount),
      stop_loss: 0,
      take_profit: 0,
      timer_units: parseInt(duration),
      trading_pair: pair,
      order_type: 'Conditional',
      pair: pair,
      entry_point: parseFloat(coinPrice).toFixed(6),
      side: pendingOrderSide,
      
      // Advanced order fields
      trading_type: tradingType || 'Spot',
      trigger_price: parseFloat(triggerPrice),
      conditional_type: 'Price',
      conditional_operator: 'GTE',
      conditional_value: parseFloat(triggerPrice),
      
      // Futures specific
      leverage: tradingType === 'Futures' ? parseFloat(leverage) : null,
      margin_type: tradingType === 'Futures' ? marginType : null,
    };
    
    console.log('Final Conditional order data:', orderData);
    submitOrder(orderData);
  };

  const getSelectedPlan = () => {
    if (!tradingPlans || !Array.isArray(tradingPlans)) {
      return null;
    }
    return tradingPlans.find(plan => plan.id.toString() === duration.toString());
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Cross/Isolated and Leverage - Only for Futures */}
      {tradingType === 'Futures' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StyledSelect 
            size="small" 
            value={marginType}
            onChange={(e) => setMarginType(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </StyledSelect>
          <StyledSelect 
            size="small" 
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="10">10x</MenuItem>
            <MenuItem value="20">20x</MenuItem>
            <MenuItem value="50">50x</MenuItem>
            <MenuItem value="100">100x</MenuItem>
          </StyledSelect>
        </Box>
      )}

      {/* Trigger Price */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Trigger Price (USDT)
        </Typography>
        <StyledTextField
          fullWidth
          size="small"
          value={triggerPrice}
          onChange={(e) => setTriggerPrice(e.target.value)}
          placeholder={formatPrice(coinPrice)}
        />
      </Box>

      {/* Order Value */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Order Value (USDT)
        </Typography>
        <StyledTextField
          fullWidth
          size="small"
          value={amount}
          onChange={handleAmountChange}
          placeholder="0.00"
        />
      </Box>

      {/* Value Slider */}
      <Box sx={{ mb: 1.5, px: 0.5 }}>
        <Slider
          value={quantitySlider}
          onChange={handleSliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(percentage => (
            <Typography 
              key={percentage} 
              variant="caption" 
              sx={{ 
                color: '#848E9C', 
                fontSize: '9px',
                cursor: 'pointer',
                '&:hover': { color: '#F7A600' }
              }}
              onClick={() => handlePercentageClick(percentage)}
            >
              {percentage}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Duration */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              Select Duration
            </MenuItem>
            {tradingPlans && Array.isArray(tradingPlans) && tradingPlans.length > 0 && tradingPlans.map(plan => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.duration}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Order Summary */}
      <Box sx={{ 
        bgcolor: '#181A20', 
        borderRadius: 1, 
        p: 1, 
        mb: 1.5,
        border: '1px solid #2B3139'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Order Value
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {calculateOrderValue()} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Trigger Price
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {formatPrice(triggerPrice)} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Fee
          </Typography>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            {formatPrice(parseFloat(amount || 0) * 0.001)} USDT
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 1.5, p: 1, bgcolor: '#2B1B17', borderRadius: 1, border: '1px solid #F6465D' }}>
          <Typography variant="caption" sx={{ color: '#F6465D', fontSize: '11px' }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Buy/Sell Buttons */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('buy')}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#0ECB81',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1.2,
            '&:hover': { bgcolor: '#0BB870' },
            '&:disabled': { bgcolor: '#2B3139', color: '#848E9C' }
          }}
        >
          {isSubmitting && pendingOrderSide === 'buy' ? 'Placing...' : 'Buy/Long'}
        </Button>
        
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('sell')}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#F6465D',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1.2,
            '&:hover': { bgcolor: '#E13C52' },
            '&:disabled': { bgcolor: '#2B3139', color: '#848E9C' }
          }}
        >
          {isSubmitting && pendingOrderSide === 'sell' ? 'Placing...' : 'Sell/Short'}
        </Button>
      </Box>

      {/* Confirmation Drawer */}
      <Drawer
        anchor="bottom"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1E2329',
            color: 'white',
            borderRadius: '16px 16px 0 0',
            maxHeight: '80vh',
            zIndex: 99999,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
          }
        }}
        ModalProps={{
          sx: {
            zIndex: 99999,
          }
        }}
        sx={{
          zIndex: 99999,
          '& .MuiDrawer-paper': {
            zIndex: 99999,
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Confirm Conditional Order
            </Typography>
            <IconButton 
              onClick={() => setConfirmationOpen(false)}
              sx={{ color: '#848E9C' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: '#2B3139', mb: 2 }} />

          {/* Order Details */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#848E9C', display: 'block', mb: 1 }}>
              Order Details
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Side:</Typography>
              <Typography variant="body2" sx={{ 
                color: pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D',
                fontWeight: 'bold'
              }}>
                {pendingOrderSide === 'buy' ? 'Buy/Long' : 'Sell/Short'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Type:</Typography>
              <Typography variant="body2">Conditional</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Pair:</Typography>
              <Typography variant="body2">{pair}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Trigger Price:</Typography>
              <Typography variant="body2">{triggerPrice} USDT</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Amount:</Typography>
              <Typography variant="body2" fontWeight="bold">{amount} USDT</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Duration:</Typography>
              <Typography variant="body2">{getSelectedPlan()?.duration || 'N/A'}</Typography>
            </Box>
          </Box>

          {/* Confirmation Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConfirmationOpen(false)}
              sx={{
                borderColor: '#2B3139',
                color: '#848E9C',
                '&:hover': { borderColor: '#F7A600', color: '#F7A600' }
              }}
            >
              Cancel
            </Button>
            
            <Button
              fullWidth
              variant="contained"
              onClick={confirmOrder}
              disabled={isSubmitting}
              sx={{
                bgcolor: pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D',
                color: 'white',
                '&:hover': { 
                  bgcolor: pendingOrderSide === 'buy' ? '#0BB870' : '#E13C52' 
                }
              }}
            >
              {isSubmitting ? 'Placing Order...' : 'Confirm Order'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Conditional;
